/**
 * Phosphor Icons Integration für Element-Bibliothek
 * Dynamisches Laden von 9.000+ Icons in 6 Stil-Varianten
 */

import * as PhosphorIcons from '@phosphor-icons/react';

export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';

export interface PhosphorIconMeta {
  name: string;
  displayName: string;
  tags: string[];
  category: string;
}

// Icon-Kategorien für Phosphor
export const PHOSPHOR_CATEGORIES = [
  { id: 'popular', name: 'Beliebt', icon: '⭐' },
  { id: 'arrows', name: 'Pfeile', icon: '→' },
  { id: 'commerce', name: 'E-Commerce', icon: '🛒' },
  { id: 'communication', name: 'Kommunikation', icon: '💬' },
  { id: 'design', name: 'Design', icon: '🎨' },
  { id: 'development', name: 'Entwicklung', icon: '💻' },
  { id: 'editor', name: 'Editor', icon: '✏️' },
  { id: 'finance', name: 'Finanzen', icon: '💰' },
  { id: 'health', name: 'Gesundheit', icon: '❤️' },
  { id: 'map', name: 'Karten', icon: '📍' },
  { id: 'media', name: 'Medien', icon: '🎬' },
  { id: 'nature', name: 'Natur', icon: '🌿' },
  { id: 'objects', name: 'Objekte', icon: '📦' },
  { id: 'people', name: 'Menschen', icon: '👤' },
  { id: 'social', name: 'Social Media', icon: '📱' },
  { id: 'system', name: 'System', icon: '⚙️' },
  { id: 'weather', name: 'Wetter', icon: '☀️' },
];

// Beliebte Icons für Schnellzugriff
export const POPULAR_ICONS = [
  'Heart', 'Star', 'Check', 'X', 'Plus', 'Minus',
  'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown',
  'CaretRight', 'CaretLeft', 'CaretUp', 'CaretDown',
  'House', 'User', 'Users', 'Gear', 'MagnifyingGlass',
  'Bell', 'Envelope', 'ChatCircle', 'Phone', 'Camera',
  'Image', 'Play', 'Pause', 'Stop', 'SkipForward',
  'ShoppingCart', 'Bag', 'CreditCard', 'Wallet',
  'Calendar', 'Clock', 'Timer', 'Alarm',
  'MapPin', 'Globe', 'Compass', 'Navigation',
  'Sun', 'Moon', 'Cloud', 'Lightning',
  'Fire', 'Drop', 'Leaf', 'Tree', 'Flower',
  'Eye', 'EyeSlash', 'Lock', 'LockOpen', 'Key',
  'Trash', 'Pencil', 'NotePencil', 'Clipboard',
  'File', 'Folder', 'Download', 'Upload', 'Link',
  'Share', 'Export', 'Copy', 'Scissors',
  'Sparkle', 'Lightning', 'Rocket', 'Trophy',
  'ThumbsUp', 'ThumbsDown', 'Smiley', 'SmileyWink',
  'InstagramLogo', 'FacebookLogo', 'TwitterLogo', 'LinkedinLogo',
  'YoutubeLogo', 'TiktokLogo', 'WhatsappLogo', 'TelegramLogo',
];

// Kategorisierung der Icons
const ICON_CATEGORIES: Record<string, string[]> = {
  arrows: [
    'Arrow', 'Caret', 'CaretCircle', 'CaretDouble', 'ArrowBendDown', 'ArrowBendUp',
    'ArrowCircle', 'ArrowElbow', 'ArrowFat', 'ArrowLine', 'ArrowSquare', 'ArrowU',
    'ArrowsClockwise', 'ArrowsCounter', 'ArrowsDown', 'ArrowsHorizontal', 'ArrowsIn',
    'ArrowsLeft', 'ArrowsMerge', 'ArrowsOut', 'ArrowsVertical', 'Swap', 'Shuffle',
  ],
  commerce: [
    'ShoppingCart', 'ShoppingBag', 'Bag', 'Basket', 'Storefront', 'Receipt',
    'Barcode', 'QrCode', 'CreditCard', 'Wallet', 'Money', 'CurrencyDollar',
    'CurrencyEur', 'CurrencyBtc', 'Percent', 'Tag', 'Gift', 'Package',
  ],
  communication: [
    'Chat', 'ChatCircle', 'ChatDots', 'ChatTeardrop', 'ChatText', 'Chats',
    'Envelope', 'EnvelopeOpen', 'EnvelopeSimple', 'At', 'Phone', 'PhoneCall',
    'PhoneIncoming', 'PhoneOutgoing', 'Voicemail', 'Megaphone', 'Bell', 'BellRinging',
  ],
  design: [
    'Palette', 'PaintBrush', 'PaintBucket', 'Eyedropper', 'Swatches', 'Gradient',
    'Circle', 'Square', 'Triangle', 'Rectangle', 'Polygon', 'Star', 'Heart',
    'Diamond', 'Hexagon', 'Pentagon', 'Octagon', 'Spiral', 'Infinity',
    'Crop', 'Selection', 'SelectionAll', 'SelectionSlash', 'BoundingBox',
  ],
  development: [
    'Code', 'CodeBlock', 'CodeSimple', 'Terminal', 'TerminalWindow', 'Command',
    'Bug', 'BugBeetle', 'Virus', 'Database', 'HardDrive', 'Cpu', 'GitBranch',
    'GitCommit', 'GitDiff', 'GitFork', 'GitMerge', 'GitPullRequest', 'Brackets',
  ],
  editor: [
    'TextT', 'TextAa', 'TextB', 'TextItalic', 'TextUnderline', 'TextStrikethrough',
    'TextAlignLeft', 'TextAlignCenter', 'TextAlignRight', 'TextAlignJustify',
    'TextIndent', 'TextOutdent', 'ListBullets', 'ListNumbers', 'ListDashes',
    'Table', 'Rows', 'Columns', 'SquaresFour', 'GridFour', 'Layout',
  ],
  finance: [
    'Bank', 'Vault', 'PiggyBank', 'Coins', 'Money', 'CurrencyCircleDollar',
    'CurrencyDollar', 'CurrencyEur', 'CurrencyGbp', 'CurrencyYen', 'CurrencyBtc',
    'CurrencyEth', 'ChartLine', 'ChartLineUp', 'ChartLineDown', 'ChartBar',
    'ChartPie', 'TrendUp', 'TrendDown', 'Percent', 'Calculator', 'Invoice',
  ],
  health: [
    'Heart', 'Heartbeat', 'FirstAid', 'FirstAidKit', 'Pill', 'Prescription',
    'Stethoscope', 'Thermometer', 'Syringe', 'Bandaids', 'Virus', 'Bacteria',
    'Dna', 'Brain', 'Tooth', 'Wheelchair', 'Hospital', 'Ambulance',
  ],
  map: [
    'MapPin', 'MapPinLine', 'MapTrifold', 'Globe', 'GlobeHemisphereEast',
    'GlobeHemisphereWest', 'Compass', 'Navigation', 'NavigationArrow',
    'Road', 'RoadHorizon', 'Path', 'Signpost', 'Flag', 'FlagBanner',
  ],
  media: [
    'Play', 'Pause', 'Stop', 'Record', 'SkipBack', 'SkipForward', 'Rewind',
    'FastForward', 'Repeat', 'RepeatOnce', 'Shuffle', 'SpeakerHigh', 'SpeakerLow',
    'SpeakerNone', 'SpeakerSlash', 'Microphone', 'MicrophoneSlash', 'Camera',
    'CameraSlash', 'Video', 'VideoCamera', 'Film', 'FilmSlate', 'FilmStrip',
    'Image', 'Images', 'ImageSquare', 'Picture', 'Aperture', 'Broadcast',
  ],
  nature: [
    'Sun', 'Moon', 'Star', 'Planet', 'Shooting', 'Cloud', 'CloudRain',
    'CloudSnow', 'CloudLightning', 'Rainbow', 'Drop', 'Waves', 'Wind',
    'Leaf', 'Tree', 'TreeEvergreen', 'Flower', 'FlowerLotus', 'Butterfly',
    'Bird', 'Fish', 'Paw', 'Cat', 'Dog', 'Mountains', 'Campfire',
  ],
  objects: [
    'Lightbulb', 'Lamp', 'Lightning', 'Flashlight', 'Battery', 'Plug',
    'Key', 'Lock', 'LockOpen', 'Shield', 'Umbrella', 'Sunglasses',
    'Eyeglasses', 'Watch', 'Alarm', 'Hourglass', 'Clock', 'Timer',
    'Coffee', 'Beer', 'Wine', 'Martini', 'ForkKnife', 'Hamburger',
    'Pizza', 'Cookie', 'IceCream', 'Cake', 'Orange', 'Apple',
  ],
  people: [
    'User', 'UserCircle', 'UserSquare', 'Users', 'UsersFour', 'UsersThree',
    'UserPlus', 'UserMinus', 'UserGear', 'Person', 'PersonSimple', 'Baby',
    'Student', 'GraduationCap', 'Briefcase', 'IdentificationCard', 'AddressBook',
  ],
  social: [
    'InstagramLogo', 'FacebookLogo', 'TwitterLogo', 'LinkedinLogo', 'YoutubeLogo',
    'TiktokLogo', 'WhatsappLogo', 'TelegramLogo', 'DiscordLogo', 'SlackLogo',
    'SnapchatLogo', 'PinterestLogo', 'RedditLogo', 'TwitchLogo', 'SpotifyLogo',
    'AppleLogo', 'GoogleLogo', 'MicrosoftOutlookLogo', 'PaypalLogo', 'StripeLogo',
  ],
  system: [
    'Gear', 'GearSix', 'Faders', 'FadersHorizontal', 'Sliders', 'SlidersHorizontal',
    'Wrench', 'Hammer', 'Screwdriver', 'Toolbox', 'Power', 'Activity',
    'Pulse', 'Gauge', 'Spinner', 'CircleNotch', 'DotsThree', 'DotsThreeVertical',
    'DotsSix', 'DotsNine', 'List', 'Rows', 'Columns', 'Question', 'Info',
    'Warning', 'WarningCircle', 'WarningDiamond', 'ProhibitInset', 'Check',
    'CheckCircle', 'X', 'XCircle', 'Plus', 'Minus', 'Equals', 'NotEquals',
  ],
  weather: [
    'Sun', 'SunDim', 'SunHorizon', 'Moon', 'MoonStars', 'Cloud', 'CloudSun',
    'CloudMoon', 'CloudRain', 'CloudSnow', 'CloudFog', 'CloudLightning',
    'Rainbow', 'Thermometer', 'ThermometerHot', 'ThermometerCold', 'Wind',
    'Snowflake', 'Drop', 'Umbrella', 'UmbrellaSimple',
  ],
};

// Helper to check if something is a React component
function isReactComponent(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false;
  const component = obj as { $$typeof?: symbol; render?: unknown };
  // Check for forwardRef components (Symbol(react.forward_ref))
  if (component.$$typeof && typeof component.$$typeof === 'symbol') {
    return component.$$typeof.toString().includes('react');
  }
  // Check for function components
  if (typeof component === 'function') return true;
  return false;
}

// Get all available Phosphor icon names
export function getPhosphorIconNames(): string[] {
  const icons = PhosphorIcons as Record<string, unknown>;
  return Object.keys(icons).filter(
    key => key !== 'IconContext' &&
           key !== 'IconBase' &&
           key !== 'SSRBase' &&
           key !== 'IconWeight' &&
           key !== 'default' &&
           !key.startsWith('Phosphor') &&
           !key.endsWith('Icon') && // Skip *Icon variants (duplicates)
           isReactComponent(icons[key])
  );
}

// Get icon component by name
export function getPhosphorIcon(name: string): React.ComponentType<{ size?: number; weight?: IconWeight; color?: string }> | null {
  const icons = PhosphorIcons as Record<string, unknown>;
  const icon = icons[name];
  if (isReactComponent(icon)) {
    return icon as React.ComponentType<{ size?: number; weight?: IconWeight; color?: string }>;
  }
  return null;
}

// Get icons by category
export function getIconsByCategory(categoryId: string): string[] {
  if (categoryId === 'popular') {
    return POPULAR_ICONS.filter(name => getPhosphorIcon(name) !== null);
  }

  if (categoryId === 'all') {
    return getPhosphorIconNames();
  }

  const patterns = ICON_CATEGORIES[categoryId] || [];
  const allIcons = getPhosphorIconNames();

  return allIcons.filter(iconName =>
    patterns.some(pattern => iconName.includes(pattern))
  );
}

// Search icons
export function searchPhosphorIcons(query: string): string[] {
  const q = query.toLowerCase();
  const allIcons = getPhosphorIconNames();

  return allIcons.filter(iconName => {
    // Convert camelCase to searchable string
    const searchable = iconName.replace(/([A-Z])/g, ' $1').toLowerCase();
    return searchable.includes(q) || iconName.toLowerCase().includes(q);
  });
}

// Convert icon name to display name
export function getIconDisplayName(name: string): string {
  // Convert camelCase to spaces
  return name.replace(/([A-Z])/g, ' $1').trim();
}

// Icon weight labels for German UI
export const WEIGHT_LABELS: Record<IconWeight, string> = {
  thin: 'Dünn',
  light: 'Leicht',
  regular: 'Normal',
  bold: 'Fett',
  fill: 'Gefüllt',
  duotone: 'Duotone',
};

// Total icon count
export function getPhosphorIconCount(): number {
  return getPhosphorIconNames().length;
}
